import React, { useCallback, useMemo } from "react";
import { getFleetPerformanceAnalyticsList } from "../../../Api/FleetPerformanceApi";
import { FLEET_CHART_CONFIG } from "./FleetChartConfig";
import FleetBreakdownChart from "../Components/FleetBreakdownChart";
import { formatCompactEnergy } from "../Components/FleetCommonFunctions";
import { FLEET_PERFORMANCE_CHART_KEYS, resolveFleetGroupByField, useFleetBreakdownDimensions } from "../fleetBreakdownDimensions";

const EnergyFlowBreakdownChart = ({ startDate, endDate, globalFilters }) => {
    const groupByOptions = useFleetBreakdownDimensions(FLEET_PERFORMANCE_CHART_KEYS.ENERGY_FLOW_BREAKDOWN);
    const defaultGroupBy = groupByOptions[0]?.value;

    const handleFetchSidebarData = useCallback(async (groupBy) => {
        try {
            const backendGroupBy = resolveFleetGroupByField(groupBy); // the dimension's own value is the backend field
            const apiParams = { groupBy: [backendGroupBy], select: ['DistinctVehicleCount'], startDate, endDate, sortBy: JSON.stringify([{ column: 'DistinctVehicleCount', direction: 'DESC' }]) };
            const response = await getFleetPerformanceAnalyticsList(globalFilters, apiParams);
            if (response?.responseStatus?.code === 200) {
                const data = response.response?.data || [];
                const items = data.map(item => ({ id: item.xLabel, label: item.xLabel, count: (item.metricsArray || []).reduce((sum, m) => sum + (m.value || 0), 0) }));
                const sortedItems = items.slice().sort((a, b) => {
                    if (!a.label && b.label) return 1;
                    if (a.label && !b.label) return -1;
                    return (a.label || '').localeCompare(b.label || '');
                });
                return { items: [{ id: 'all', label: 'All', count: items.reduce((sum, i) => sum + i.count, 0) }, ...sortedItems] };
            }
            return { items: [] };
        } catch (e) { return { items: [] }; }
    }, [startDate, endDate, globalFilters]);

    const handleFetchChartData = useCallback(async (groupBy, selectedItems) => {
        try {
            const backendGroupBy = resolveFleetGroupByField(groupBy); // the dimension's own value is the backend field
            const isAllSelected = selectedItems.some(i => i.id === 'all');
            const filters = !isAllSelected && selectedItems.length > 0 ? [{ name: backendGroupBy, values: selectedItems.map(i => i.id) }] : [];
            const apiParams = { groupBy: ['ProjectName'], select: ['ChargeEnergyConsumedTotal', 'DischargeEnergyConsumedTotal', 'RegenEnergyTotal'], startDate, endDate, filters: filters.length > 0 ? filters : undefined };
            const response = await getFleetPerformanceAnalyticsList(globalFilters, apiParams);
            if (response?.responseStatus?.code === 200) {
                return (response.response?.data || []).map(item => {
                    const metrics = item.metricsArray || [];
                    const getValue = (t) => metrics.find(m => m.type === t)?.value || 0;
                    return { label: item.xLabel, charge: getValue("ChargeEnergyConsumedTotal"), discharge: getValue("DischargeEnergyConsumedTotal"), regen: getValue("RegenEnergyTotal") };
                }).sort((a, b) => a.label.localeCompare(b.label));
            }
            return [];
        } catch (e) { return []; }
    }, [startDate, endDate, globalFilters]);

    const getChartConfig = useCallback((chartData) => {
        if (!chartData || chartData.length === 0) return { series: [], options: {} };
        const categories = chartData.map(d => d.label);
        const allValues = chartData.flatMap(d => [d.charge, d.discharge, d.regen]).filter(v => v > 0);
        const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
        const { unit, divisor } = formatCompactEnergy(minValue);
        return {
            series: [
                { name: `Charge Energy Consumption (${unit})`, data: chartData.map(d => Math.round(d.charge / divisor)) },
                { name: `Discharge Energy Consumption (${unit})`, data: chartData.map(d => Math.round(d.discharge / divisor)) },
                { name: `Regeneration Energy Recovered (${unit})`, data: chartData.map(d => Math.round(d.regen / divisor)) }
            ],
            options: {
                chart: { id: "energy-flow-breakdown-chart", stacked: false },
                colors: ["#D47CAF", "#7A3CC9", "#0C7A1F"],
                xaxis: { categories, labels: { show: true } },
                plotOptions: { bar: { horizontal: true, barHeight: "14px", borderRadius: 1 } },
                tooltip: { y: { formatter: (val) => val + ` ${unit}` } }
            },
            xAxisTitle: `Energy Consumption (${unit})`,
            listHeader: "NO. OF VEHICLES",
            customLegend: {
                chartId: "energy-flow-breakdown-chart",
                label: "Project",
                centered: true,
                series: [{ name: `Charge Energy Consumption (${unit})`, color: "#D47CAF" }, { name: `Discharge Energy Consumption (${unit})`, color: "#7A3CC9" }, { name: `Regeneration Energy Recovered (${unit})`, color: "#0C7A1F" }]
            }
        };
    }, []);

    const config = useMemo(() => ({
        onFetchSidebarData: handleFetchSidebarData,
        onFetchChartData: handleFetchChartData,
        getChartConfig
    }), [handleFetchSidebarData, handleFetchChartData, getChartConfig]);

    return (
        <FleetBreakdownChart
            minVisibilityHeight={400}
            title="Energy Flow breakdown Analysis"
            // Keyed on the default: FleetBreakdownChart seeds its groupBy state from these
            // props on mount only, and the page's meta lands after that. The key is the same
            // string before and after for an org that does not drop the mounted dimension.
            key={defaultGroupBy}
            groupByOptions={groupByOptions}
            defaultGroupBy={defaultGroupBy}
            xAxisTitle="Energy Consumption (kWh)"
            listHeader="NO. OF VEHICLES"
            config={config}
            dependencies={[startDate, endDate, globalFilters]}
            labelPrefix="energy-flow-label"
            barGap={40}
            fixedBarHeight={40}
            drillDownConfig={FLEET_CHART_CONFIG.ENERGY_FLOW_BREAKDOWN}
        />
    );
};

export default EnergyFlowBreakdownChart;
